import {RouteVisit, reconcileVisits} from './route-visits';
import {FlightLeg} from "../../schema";
import {getOptimalFlightPathSubset} from "./flight-leg-manager";

export interface FlightPathOptionalArgs {

    /**
     * @default false
     */
    preventDupeNodeInsertion?: boolean

    /**
     * Whether the route should implicitly close by returning to its origin.
     * @default true
     */
    returnToOrigin?: boolean
}


export class FlightPath {

    private visits: RouteVisit[] = [];
    getVisits(): RouteVisit[] {
        this.visits = reconcileVisits(this.visits, this.getRoutePath());
        return this.visits;
    }
    setVisits(visits: RouteVisit[]) { this.visits = visits; }
    private origin: string;
    private originalOrigin: string;
    private path: Array<string> = [];
    public preventDupeNodeInsertion = false;
    public returnToOrigin = true;

    constructor(origin?: string, optionalArgs?: FlightPathOptionalArgs) {
        this.origin = origin;

        if (typeof optionalArgs?.preventDupeNodeInsertion === 'boolean'){
            this.preventDupeNodeInsertion = optionalArgs.preventDupeNodeInsertion;
        }
        if (typeof optionalArgs?.returnToOrigin === 'boolean') {
            this.returnToOrigin = optionalArgs.returnToOrigin;
        }
    }

    static fromFlightLegs(legs: FlightLeg[], overrideOrigin?: string, optionalArgs?: FlightPathOptionalArgs): FlightPath {

        let origin = overrideOrigin || legs[0]?.departureID;

        let originOverridden = overrideOrigin && overrideOrigin !== legs[0]?.departureID;
        let originalOrigin = legs[0]?.departureID;

        const returnToOrigin = optionalArgs?.returnToOrigin ?? (
            legs.length > 0 && legs[legs.length - 1].destinationID === originalOrigin
        );
        const flightPath = new FlightPath(origin, {...optionalArgs, returnToOrigin});
        flightPath.originalOrigin = originalOrigin;
        legs.forEach((leg, idx) => {
            const destination = originOverridden && leg.destinationID === originalOrigin
                ? origin : leg.destinationID;
            // Only an explicit request to open a round trip removes its final closure.
            if (optionalArgs?.returnToOrigin === false && idx === legs.length - 1 &&
                destination === origin && legs.length > 1) return;
            flightPath.addNextNode(destination, true);
        });

        return flightPath;
    }

    static fromLocationList<T extends { _id: string, name?: string }>(locations: T[], optionalArgs?: FlightPathOptionalArgs): FlightPath {

        const returnToOrigin = optionalArgs?.returnToOrigin ?? (
            locations.length > 1 && locations[0]?._id === locations[locations.length - 1]?._id
        );
        let flightPath = new FlightPath(undefined, {...optionalArgs, returnToOrigin});

        locations.forEach((loc) => {
            let id = loc?._id;
            flightPath.addNextNode(id, true);
        })

        return flightPath;
    }

    removeNode(locID: string) {
        if (locID === this.origin) {
            // If there is only one location in the round trip and you remove the leg that is inbound to the origin,
            // remove the location from the path.
            if (this.path.length === 1) {
                this.path = [];
            }
            // Otherwise we can't remove the origin
            return;
        }
        let nodeIdx = this.path.findIndex((node) => locID === node);
        if (nodeIdx > -1) {
            this.path.splice(nodeIdx, 1);
        }
    }

    /**
     * Adds a new location node in the path if not in path.
     * If origin was not defined, then this node will become the origin.
     * @param locID
     * @param forceAdd If true, forces a location to be added to the path.
     * @returns Index of location node in flight path
     */
    addNextNode(locID: string, forceAdd?: boolean): number {
        if (!this.origin) {
            this.origin = locID;
            return 0;
        } else if (this.path.includes(locID) && this.preventDupeNodeInsertion && !forceAdd) {
            return this.path.findIndex((id) => id === locID) + 1 // +1 taking into account the origin
        } else if (locID === this.origin && !forceAdd) {
            return 0; // This is the origin, so don't insert anything.
        } else {
            this.path.push(locID);
            return this.path.length // Why not subtract 1? We are taking into account the origin, so -1 + 1 = 0;
        }
    }

    insertNode(departure: string, destination: string) {
        let subset = this.getNodeSubset(departure, destination);
        if (subset && subset.length > 0) {
            // A route for this departure and destination exists. No need to insert anything new
            return;
        }
        let depIdx = this.getNodeIdx(departure);
        let destIdx = this.getNodeIdx(destination);

        if (depIdx > destIdx && destIdx > -1) {
            // Departure and destination indexes for this entity are in reverse order.
            // Insert a new destination after the entity departure
            this.path.splice(depIdx, 0, destination);
        } else if (depIdx > -1) {
            // Departure exists in route. Insert destination to route.
            this.path.push(destination);
        } else if (destIdx > -1) {
            // Destination exists in route, but not departure. Insert departure before destination.
            this.path.splice(destIdx - 1, 0, departure);
        } else {
            // Neither departure nor destination are in the route. Insert both into route.
            this.path.push(departure);
            this.path.push(destination);
        }
    }

    getNodeIdxInSubset(locID: string, startIdx: number, endIdx: number) {
        let idx = this.path
            .slice(startIdx, endIdx)
            .findIndex((id) => id === locID);
        return idx === -1 ? -1 : startIdx + idx + 1;
    }

    getNodeIdx(locID: string) {
        if (locID === this.origin) return 0;

        let idx = this.path.findIndex((id) => id === locID);
        return idx === -1 ? -1 : idx + 1;
    }

    getNodeIdxs(locID: string) {
        if (locID === this.origin) return [0];
        let idxs: number[] = [];
        this.path.forEach((node, idx) => {
            if (node === locID) {
                idxs.push(idx + 1);
            }
        })
        return idxs;
    }

    getNodeSubset(startLoc: string, endLoc: string) {

        const path = this.getRoutePath();

        const [startIdx, endIdx] = getOptimalFlightPathSubset(path, startLoc, endLoc);

        if (endIdx - startIdx >= Infinity) {
            return []
        }

        return path.slice(startIdx, endIdx + 1)
    }

    // getNodeSubset(startLoc: string, endLoc: string){
    //     let startIdx = this.getNodeIdx(startLoc);
    //     let endIdx = this.getNodeIdx(endLoc);
    //
    //     if (startIdx > endIdx){
    //         // Reverse order found. Does the endLoc have duplicates?
    //         let idxs = this.getNodeIdxs(endLoc);
    //         if (idxs.length > 1){
    //             // Duplicate found. Find the next index that is larger than the starting index
    //             for (let i = 0; i < idxs.length; i++) {
    //                 const secondEndIdx = idxs[i];
    //                 if (secondEndIdx > startIdx){
    //                     endIdx = secondEndIdx;
    //                     break;
    //                 }
    //             }
    //         }
    //     }
    //
    //     if (startIdx === -1 || endIdx === -1){
    //         return undefined;
    //     }
    //
    //     if (startIdx === 0){
    //         // This is the origin
    //         return [ this.origin, ...this.path.slice(0, endIdx) ]
    //     }
    //     if (endIdx === 0){
    //         return [...this.path.slice(startIdx-1, this.path.length), this.origin ]
    //     }
    //     else if (endIdx === 0){
    //         return undefined
    //     }
    //     return this.path.slice(startIdx-1, endIdx)
    // }

    isRoundTrip() {
        return this.getRoutePath().slice(-1)[0] === this.origin
    }

    getPath() {
        // If there is no origin, then there shouldn't be any other nodes
        if (!this.origin || this.path.length === 0) return []

        return [this.origin, ...this.path]
    }

    getRoundTripPath() {
        // If there is no origin, then there shouldn't be any other nodes
        if (!this.origin) return []

        if (this.path[this.path.length-1] === this.origin) {
            // Path is already returning to origin
            return [
                this.origin,
                ...this.path
            ]
        }

        return [
            this.origin,
            ...this.path,
            this.origin
        ]
    }

    getRoutePath() {
        // Keep master's origin-only placeholder when no destinations remain.
        return this.returnToOrigin || this.path.length === 0
            ? this.getRoundTripPath()
            : this.getPath();
    }

    getOrigin() {
        return this.origin;
    }

    setOrigin(locID: string) {
        this.origin = locID;
    }

    /**
     * Move node to a particular index in the path
     * @param locID Location ID to move
     * @param displaceLocID Location ID to push out of the way
     * @returns Reason of move failure. If undefined, then move was successful.
     */
    moveNode(locID: string, displaceLocID: string): string {
        let nodeIdx = this.getNodeIdx(locID);
        let insertAt = this.getNodeIdx(displaceLocID);
        if (nodeIdx === 0) {
            return "Cannot move origin.";
        }
        if (insertAt === 0) {
            return "Cannot move location before origin.";
        }
        this.path.splice(nodeIdx - 1, 1);
        this.path.splice(insertAt - 1, 0, locID);
        return;
    }

    /**
     * Moves a destination by its zero-based leg index. Unlike moveNode(), this
     * remains unambiguous when the same location appears more than once.
     * The departure origin and a final return-to-origin destination stay fixed.
     */
    moveDestinationAtIndex(fromIndex: number, toIndex: number): string {
        const destinations = this.getRoutePath().slice(1);
        if (
            fromIndex < 0 || toIndex < 0 ||
            fromIndex >= destinations.length || toIndex >= destinations.length
        ) {
            return 'Cannot move a destination outside the route.';
        }
        if (fromIndex === toIndex) return;

        const finalIndex = destinations.length - 1;
        const hasFinalReturn = destinations[finalIndex] === this.origin;
        if (hasFinalReturn && fromIndex === finalIndex) {
            return 'The final return leg cannot be reordered.';
        }
        if (hasFinalReturn && toIndex === finalIndex) {
            return 'The final route position must return to the flight origin.';
        }

        const visits = [...this.getVisits()];
        visits.splice(toIndex + 1, 0, visits.splice(fromIndex + 1, 1)[0]);
        this.visits = visits;
        const [movedDestination] = destinations.splice(fromIndex, 1);
        destinations.splice(toIndex, 0, movedDestination);

        this.path = destinations;
        return;
    }

    /**
     * Removes the destination reached by a zero-based leg index. Addressing
     * the occurrence rather than the location ID is important for routes that
     * visit the same facility more than once.
     */
    removeDestinationAtIndex(legIndex: number): string {
        const destinations = this.getRoutePath().slice(1);
        if (legIndex < 0 || legIndex >= destinations.length) {
            return 'Cannot remove a destination outside the route.';
        }

        const finalIndex = destinations.length - 1;
        const hasFinalReturn = destinations[finalIndex] === this.origin;
        if (hasFinalReturn && legIndex === finalIndex) {
            return 'The final return leg cannot be removed.';
        }

        this.visits = this.getVisits().filter((_, index) => index !== legIndex + 1);
        destinations.splice(legIndex, 1);
        this.path = destinations;
        return;
    }

    getOriginalOrigin() {
        return this.originalOrigin;
    }
}
